














/**
 * 级联组件单选
 */
import { defineComponent, PropType, watch, toRefs, onMounted, ref, Ref, computed } from '@vue/composition-api';
import { ITreeOption, NO_LIMIT } from './const/fastCreation';
import Popup from './popup.vue';

// tree组件暴露的值节点
interface TreeValueNode {
  value: string | number;
  children?: TreeValueNode[];
  status: 'all' | 'partial';
}

const name = 'popup-tree';
export default defineComponent({
  name,
  components: { Popup },
  emits: ['confirm', 'close'],
  props: {
    show: {
      type: Boolean,
      required: true,
      default: false,
    },
    title: {
      type: String,
      default: '',
    },
    flatMode: {
      type: Boolean,
      default: true,
    },
    value: {
      type: Array as PropType<string[] | number[]>,
      default: () => [],
    },
    options: {
      type: Array as PropType<ITreeOption[]>,
      default: () => [],
    },
    treeNodesMap: {
      type: Object,
      required: true,
    },
  },
  setup(props, { emit }) {
    let selected: Ref<Array<string | number>> = ref([]);
    let origin: Ref<any> = ref();

    const { flatMode, value, options, treeNodesMap } = toRefs(props);

    const leafIdsMap = computed(() => {
      const result = {};
      const nodes = Object.values(treeNodesMap.value);

      function getLeafIds(id: string | number): Array<string | number> {
        let leafIds: Array<string | number> = [];
        const children = nodes.filter(item => item.parentId === id);
        if (children.length === 0) {
          return [id];
        }
        children.forEach(item => {
          leafIds = leafIds.concat(getLeafIds(item.value));
        });
        return leafIds;
      }

      for (let i = 0; i < nodes.length; i += 1) {
        // eslint-disable-next-line @typescript-eslint/no-shadow
        const { value } = nodes[i];
        result[value] = getLeafIds(value);
      }
      return result;
    });

    const transform2Leafs = (values: Array<string | number> = []) => {
      // eslint-disable-next-line @typescript-eslint/no-shadow
      const leafs = values.reduce((result, value) => result.concat(leafIdsMap.value[value] || [value]), []);
      return Array.from(new Set(leafs));
    };
    watch(value, (val: Array<string | number>): void => {
      selected.value = transform2Leafs(val);
      origin.value = selected.value;
    });

    watch(options, (): void => {
      selected.value = transform2Leafs(value.value);
      origin.value = selected.value;
    });

    onMounted(() => {
      selected.value = transform2Leafs(value.value);
      origin.value = selected.value;
    });

    const handleChange = (treeValues: any): void => {
      const oldVal = selected.value;
      const newVal = treeValues;

      if (oldVal.indexOf(NO_LIMIT.value) > -1) {
        selected.value = (newVal as string[]).filter(v => v !== NO_LIMIT.value);
        return;
      }

      if (oldVal.indexOf(NO_LIMIT.value) === -1 && newVal.indexOf(NO_LIMIT.value) > -1) {
        selected.value = [NO_LIMIT.value];
        return;
      }

      selected.value = newVal;
    };

    // 是否叶子结点
    const isLeaf = id => {
      const leafIds = leafIdsMap[id] || [];
      return leafIds[0] === id && leafIds.length === 1;
    };

    const checkedToThroughValue = checked => {
      let orphans = checked;
      const result: any = [];
      const _leafIdsMap = leafIdsMap.value;

      // 按子树规模倒序排列
      const treeNodes = Object.values(treeNodesMap.value).sort(
        (prev, next) => (_leafIdsMap[next.id] || []).length - (_leafIdsMap[prev.id] || []).length,
      );
      for (let i = 0; i < treeNodes.length; i += 1) {
        if (orphans.length === 0) {
          break;
        }
        const { id = '' } = treeNodesMap.value[treeNodes[i].id] || {};
        const leafIds = _leafIdsMap[id] || [];
        if (isLeaf(id)) {
          // 叶子节点单选
          if (orphans.includes(id)) {
            result.push(id);
            orphans = orphans.filter(s => s !== id);
          }
        } else {
          // 子节点全选，使用合并后的父节点
          // eslint-disable-next-line
          if (leafIds.every((leaf) => orphans.includes(leaf))) {
            result.push(id);
            orphans = orphans.filter(s => !leafIds.includes(s));
            // eslint-disable-next-line
          } else if (leafIds.some((leaf) => orphans.includes(leaf))) {
            // 子节点部分选择
            result.push(id);
            orphans = orphans.filter(s => s !== id);
          }
        }
      }
      return result;
    };

    // 获取节点深度
    const getNodeLevel = id => {
      let result = 0;
      const { parentId } = treeNodesMap.value[id] || {};
      if (!parentId) {
        return 0;
      }
      result += 1;
      result += getNodeLevel(parentId);
      return result;
    };

    function checkedToValue(checked) {
      // source为异步加载时，可能先拿到了value，但source还在拉取中，此时value需要保留住
      if (!Object.keys(treeNodesMap.value).length) {
        return checked.slice();
      }

      let orphans = checked.slice();
      let orphanSet = new Set(orphans);
      const result: any = [];

      const getLowestCheckedAncestor = id => {
        const { parentId } = treeNodesMap.value[id] || {};
        if (!parentId) {
          return id;
        }
        const leafs = leafIdsMap.value[parentId] || [];

        // eslint-disable-next-line @typescript-eslint/no-shadow
        const isAllSelected = leafs.every(id => orphanSet.has(id));

        if (!isAllSelected) {
          return id;
        }
        return getLowestCheckedAncestor(parentId);
      };

      const removeAncestorLeafs = ancestor => {
        const leafIds = leafIdsMap.value[ancestor] || [ancestor];

        orphans = orphans.filter(id => !leafIds.includes(id));
        orphanSet = new Set(orphans);
      };

      while (orphans.length) {
        const first = orphans[0];
        const ancestor = getLowestCheckedAncestor(first);
        result.push(ancestor);
        removeAncestorLeafs(ancestor);
      }
      return result;
    }

    const mergedValues = () => checkedToValue(selected.value);

    const throughedValues = () => {
      const result: any = [];
      const throughedIds = checkedToThroughValue(selected.value) || [];
      throughedIds.forEach(id => {
        const level = getNodeLevel(id);
        result[level] = result[level] || [];
        result[level].push(id);
      });
      return result;
    };

    const closePopup = (): void => {
      // 解决数据变更取消时回显出入
      selected.value = origin.value;
      emit('close');
    };

    const handleCancel = (): void => {
      closePopup();
    };

    const handleConfirm = (): void => {
      origin.value = selected.value;
      if (flatMode.value) {
        emit('confirm', selected.value);
      } else {
        emit('confirm', {
          mergedValues: mergedValues(),
          throughedValues: throughedValues(),
          checkedLeafs: selected.value,
        });
      }
      closePopup();
    };
    return {
      name,
      handleCancel,
      handleConfirm,
      handleChange,
      selected,
    };
  },
});
